import React, { useEffect } from "react";
import styles from "./DatePicker.module.css";

// Default .NET MinValue sentinel in UTC
const DEFAULT_UNSET_SENTINEL = new Date(Date.UTC(1, 0, 1, 0, 0, 0, 0));

const isUnsetSentinel = (
  v?: Date | string,
  sentinel: Date | string = DEFAULT_UNSET_SENTINEL
): boolean => {
  if (!v) return false;

  if (v instanceof Date) {
    if (isNaN(v.getTime())) return false;
    // Consider either exact match or any year <= 1 as unset
    const vTime = v.getTime();
    const sTime = sentinel instanceof Date ? sentinel.getTime() : DEFAULT_UNSET_SENTINEL.getTime();
    return v.getUTCFullYear() <= 1 || vTime === sTime;
  }

  // string
  if (typeof v === "string") {
    // Most robust check: starts with 0001-01-01 (covers ISO strings)
    if (v.startsWith("0001-01-01")) return true;
  }
  return false;
};

const toUtcInputValue = (d: Date): string => {
  // Format YYYY-MM-DD using UTC to avoid TZ shifts
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

interface DatePickerProps {
  label?: string;
  value?: Date | string;
  onChange?: (date: Date) => void;
  readonly?: boolean;
  unsetSentinel?: Date | string;
  autoDefaultToday?: boolean; // ✅ add
}

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  value,
  onChange,
  readonly,
  unsetSentinel = DEFAULT_UNSET_SENTINEL,
  autoDefaultToday = false,
}) => {
  useEffect(() => {
    if (!autoDefaultToday) return;
    if (onChange && isUnsetSentinel(value, unsetSentinel)) {
      onChange(new Date());
    }
  }, [value, onChange, unsetSentinel, autoDefaultToday]);

  const showAsEmpty = isUnsetSentinel(value, unsetSentinel);
  let inputValue = "";

  if (!showAsEmpty) {
    if (value instanceof Date && !isNaN(value.getTime())) {
      inputValue = toUtcInputValue(value);
    } else if (typeof value === "string" && value) {
      const iso = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value;
      const d = new Date(iso);
      if (!isNaN(d.getTime())) inputValue = toUtcInputValue(d);
    }
  } else {
    // ✅ if unset, show empty (not today)
    inputValue = "";
  }

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onChange) return;
    const raw = e.target.value;
    if (!raw) {
      onChange(unsetSentinel instanceof Date ? unsetSentinel : DEFAULT_UNSET_SENTINEL);
      return;
    }
    onChange(new Date(`${raw}T00:00:00Z`));
  };

  return (
    <div className={styles.container}>
      {label && <label className={styles.label}>{label}</label>}
      <input
        disabled={readonly}
        type="date"
        lang="en-GB"
        aria-label={label}
        value={inputValue}
        onChange={handleDateChange}
        className={styles.datePicker}
      />
    </div>
  );
};

export default DatePicker;
